import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Camera, Video, Wifi, Film, Users, Heart, Award, Globe, Lock,
  Download, Phone, Check, ArrowRight, MapPin, Sparkles,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

const IMG = {
  hero: "https://customer-assets-m6fa6gv7.emergentagent.net/job_legacy-stories-hub/artifacts/vt98djbf_funeral-church-and-group-carry-coffin-in-service-2025-04-06-09-33-21-utc.webp",
  pallbearers: "https://customer-assets-m6fa6gv7.emergentagent.net/job_legacy-stories-hub/artifacts/cziec4g1_pallbearers-men-and-walking-with-coffin-in-funera-2025-04-06-07-21-37-utc.webp",
  graveside: "https://customer-assets-m6fa6gv7.emergentagent.net/job_legacy-stories-hub/artifacts/uu1h4t19_people-at-funeral-putting-down-the-coffin-at-a-fun-2025-03-27-05-37-59-utc.webp",
  legacy: "https://customer-assets-m6fa6gv7.emergentagent.net/job_legacy-stories-hub/artifacts/b45tvwsn_grandparents-playing-with-granddaughter-at-home-2025-04-04-13-07-09-utc.webp",
};

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const ADDONS = [
  { name: "Funeral Photography", price: "from $500" },
  { name: "Tribute Video", price: "$250" },
  { name: "USB Keepsake", price: "$100" },
  { name: "Additional Camera", price: "$300" },
  { name: "Pre-Service Capture", price: "$250" },
  { name: "Post-Service Capture", price: "$250" },
  { name: "Drone Video", price: "$250" },
  { name: "1 Year Hosting", price: "$200" },
];

const WHY_US = [
  { icon: Heart, t: "Compassionate", d: "We understand a funeral is not simply another event. We work quietly, respectfully and with sensitivity throughout the service." },
  { icon: Award, t: "Professional", d: "Broadcast-quality cameras, sound and lighting handled by experienced operators — so the family never notices we're there." },
  { icon: Globe, t: "Family Anywhere", d: "Relatives interstate or overseas can still participate in the farewell in real time, from anywhere in the world." },
  { icon: Lock, t: "Private", d: "Your family receives a password-protected private viewing link for invited family and friends only." },
  { icon: Download, t: "Keep the Recording", d: "The full service is professionally recorded and delivered, so you can watch again whenever you need." },
  { icon: Film, t: "Preserve Their Legacy", d: "With Family Legacy Films, you can go beyond the funeral and preserve the story of the person behind the farewell." },
];

const STEPS = [
  { n: "01", t: "Contact Us", d: "Tell us the funeral date, location and service details — by phone or online form." },
  { n: "02", t: "Choose Your Package", d: "We'll help you select the most appropriate livestream option for your family." },
  { n: "03", t: "We Prepare Everything", d: "Our team arrives early to set up cameras, audio and livestreaming — and test everything before the service." },
  { n: "04", t: "Family Watches From Anywhere", d: "A private viewing link is shared with your invited family and friends." },
  { n: "05", t: "Receive the Recording", d: "After the service, your family receives the professionally finished digital recording to keep." },
];

const emptyForm = {
  name: "", phone: "", email: "", funeral_date: "", funeral_location: "",
  service_type: "", package_interest: "", overseas_family: "", additional_info: "",
};

const UNLOCK_KEY = "flf_prices_unlocked";

function LockedPrice({ onUnlock, size = "lg" }) {
  return (
    <button type="button" onClick={onUnlock} data-testid="locked-price"
      className={`inline-flex items-center gap-2 rounded-full bg-secondary text-white ${size === "lg" ? "px-5 py-2.5 text-sm" : "px-3 py-1 text-xs"} hover:bg-secondary-hover transition-colors`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
      Unlock pricing
    </button>
  );
}

function UnlockModal({ city, defaultInterest, onClose, onUnlocked }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/pricing/unlock", { ...form, city, interested_in: defaultInterest || "" });
      localStorage.setItem(UNLOCK_KEY, "1");
      toast.success("Pricing unlocked. The team has been notified.");
      onUnlocked();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="unlock-modal">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md p-8 shadow-2xl">
        <p className="eyebrow mb-2">Funeral pricing · {city}</p>
        <h3 className="font-serif text-2xl md:text-3xl mb-2">See our full pricing.</h3>
        <p className="text-warmgrey text-sm mb-6">Leave your details and we'll unlock every package on this page. We'll also send a copy to your inbox and give you a quiet, no-pressure call.</p>
        <input required data-testid="unlock-name" className="input-field mb-3" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input required type="email" data-testid="unlock-email" className="input-field mb-3" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input required data-testid="unlock-phone" className="input-field mb-5" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <button disabled={loading} className="btn-primary w-full justify-center" data-testid="unlock-submit">
          {loading ? "Unlocking…" : "Unlock Pricing"}
        </button>
        <button type="button" onClick={onClose} className="text-xs text-warmgrey-muted mt-3 hover:text-headline block mx-auto" data-testid="unlock-close">Not now</button>
        <p className="text-[11px] text-warmgrey-muted mt-4 text-center">Your details are handled with care and never shared.</p>
      </form>
    </div>
  );
}

export default function FuneralLandingPage({ city, citySlug, packages, coverageAddons = [], areas, phone = "+61460017777", phoneDisplay = "0460 017 777" }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockContext, setUnlockContext] = useState("");

  React.useEffect(() => {
    setUnlocked(typeof window !== "undefined" && localStorage.getItem(UNLOCK_KEY) === "1");
  }, []);

  const requestUnlock = (context = "") => { setUnlockContext(context); setShowUnlock(true); };

  const cityLower = city.toLowerCase();
  const seoTitle = `Funeral Livestream ${city} | Funeral Video Services | Family Legacy Films`;
  const seoDesc = `Professional funeral livestream and funeral video services in ${city}. 2–3 camera coverage, private livestream, recording and professional production. Get a quote today.`;
  const canonical = typeof window !== "undefined" ? `${window.location.origin}/${citySlug}` : undefined;

  const FAQS = [
    { q: `How quickly can you provide a funeral livestream in ${city}?`, a: "We understand that funeral arrangements can happen quickly. Contact us as soon as you know the date and location and we'll confirm our availability. Urgent bookings are welcome, subject to availability." },
    { q: "Can family members watch from overseas?", a: "Yes. The private livestream link can be shared with family and friends anywhere in the world with a suitable internet connection — laptop, phone or tablet." },
    { q: "Is the funeral livestream recorded?", a: "Yes. A full digital recording is included with both the Essential and Professional packages, delivered privately after the service." },
    { q: "How many cameras are included?", a: "The Essential package includes 2 cameras. The Professional package includes 3 cameras with live switching. Additional cameras can be added if needed." },
    { q: "Can you record the hearse arriving and leaving?", a: "Yes. Hearse arrival and departure coverage is included in the Professional package, subject to access and location conditions." },
    { q: "Do you provide drone footage?", a: "Drone footage can be provided as part of the Professional package where legally permitted, weather conditions are suitable, and the location allows safe drone operation." },
    { q: "Can you cover a church and a funeral home?", a: "Yes. Additional-location coverage can be arranged; additional-location fees may apply." },
    { q: "Can we receive a copy of the funeral recording?", a: "Yes. A digital copy of the full service is included with both livestream packages. A USB keepsake is available as an optional add-on." },
    { q: "Can we create a Family Legacy Film as well?", a: "Yes. Families who book our funeral services may receive up to 15% off selected Family Legacy Films packages — a way to preserve not only the farewell, but the life itself." },
  ];

  useSeo({
    title: seoTitle,
    description: seoDesc,
    canonical,
    image: IMG.hero,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Funeral Livestream and Funeral Video Services",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Family Legacy Films",
        "image": IMG.hero,
        "telephone": phone,
        "email": "info@familylegacyfilms.com.au",
        "areaServed": areas.map((a) => ({ "@type": "Place", "name": a })),
        "address": { "@type": "PostalAddress", "addressLocality": city, "addressRegion": city === "Canberra" ? "ACT" : "NSW", "addressCountry": "AU" },
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `${city} Funeral Livestream Packages`,
        "itemListElement": packages.map((p) => ({
          "@type": "Offer",
          "name": p.name,
          "price": p.price,
          "priceCurrency": "AUD",
        })),
      },
    },
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const summary = [
        `City: ${city}`,
        `Funeral date: ${form.funeral_date || "—"}`,
        `Funeral location: ${form.funeral_location || "—"}`,
        `Service type: ${form.service_type || "—"}`,
        `Package interest: ${form.package_interest || "—"}`,
        `Overseas/interstate family: ${form.overseas_family || "—"}`,
        "",
        form.additional_info || "",
      ].join("\n");
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        preferred_time: form.funeral_date,
        message: summary,
        inquiry_type: `funeral_quote_${cityLower}`,
        metadata: {
          city,
          funeral_date: form.funeral_date,
          funeral_location: form.funeral_location,
          service_type: form.service_type,
          package_interest: form.package_interest,
          overseas_family: form.overseas_family,
        },
      });
      setDone(true);
      toast.success("Your quote request has been received. We will call you shortly.");
      setForm(emptyForm);
      setTimeout(() => document.getElementById("quote-thanks")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`page-funeral-${cityLower}`} className="bg-offwhite">
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden flex items-end" data-testid={`funeral-${cityLower}-hero`}>
        <img src={IMG.hero} alt={`Funeral service in a ${city} church`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,20,26,0.35) 0%, rgba(17,20,26,0.85) 100%)" }}></div>
        <div className="relative w-full pb-20 md:pb-28 pt-40">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <motion.p className="eyebrow text-primary mb-6" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}>
              Funeral Livestream &amp; Video Services · {city}
            </motion.p>
            <motion.h1
              className="font-serif text-white text-5xl md:text-7xl lg:text-8xl leading-[1.05] max-w-4xl"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9 }}
            >
              Let everyone <em style={{ color: "#C9A961", fontStyle: "italic" }}>say goodbye</em>.
            </motion.h1>
            <motion.p
              className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.9 }}
            >
              Professional funeral livestreaming and video services across {city} — helping family and friends be part of the farewell, wherever they are.
            </motion.p>

            <motion.ul
              className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.9 }}
            >
              {[
                { icon: Camera, t: "Professional multi-camera coverage" },
                { icon: Wifi, t: "Private livestream" },
                { icon: Film, t: "Full recording included" },
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-white/90 text-sm md:text-base bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                  <b.icon size={18} style={{ color: "#C9A961" }} className="mt-0.5 shrink-0" />
                  <span>{b.t}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              className="mt-10 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
            >
              <a href="#quote-form" className="btn-primary" data-testid="hero-get-quote">Get a Quote <ArrowRight size={18} /></a>
              <a href={`tel:${phone}`} className="btn-outline text-white border-white/70 hover:bg-white hover:text-secondary" data-testid="hero-call-us">
                <Phone size={16} /> Call Us
              </a>
            </motion.div>
            <p className="mt-6 text-white/60 text-sm">Available across {city} &amp; surrounding areas. Urgent bookings welcome, subject to availability.</p>
          </div>
        </div>
      </section>

      {/* EMOTIONAL INTRO */}
      <section className="section-pad bg-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <motion.div {...fadeUp}>
            <p className="eyebrow mb-4">A Farewell for Everyone</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-8 leading-tight">
              When they can't be there, we bring them closer.
            </h2>
            <p className="text-warmgrey text-lg leading-relaxed mb-4">
              When someone we love passes away, family and friends want to be together. But sometimes distance, work, health,
              travel or other circumstances make that impossible.
            </p>
            <p className="text-warmgrey text-lg leading-relaxed">
              Family Legacy Films provides professional funeral livestreaming and funeral video services across {city} and surrounding
              areas — allowing loved ones to watch, listen and be part of the farewell from anywhere in Australia or around the world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="section-pad bg-offwhite" id="packages">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div className="text-center mb-8" {...fadeUp}>
            <p className="eyebrow mb-3">Funeral Livestream Packages</p>
            <h2 className="font-serif text-4xl md:text-5xl">Choose your funeral livestream package.</h2>
            {!unlocked && (
              <div className="mt-6 inline-flex items-center gap-3 bg-white border border-border-warm rounded-full px-5 py-2.5 text-sm text-warmgrey">
                <span>Pricing is available on request.</span>
                <button onClick={() => requestUnlock("all-packages")} className="text-primary font-medium hover:underline" data-testid="unlock-inline">Unlock now →</button>
              </div>
            )}
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {packages.map((p) => (
              <motion.div key={p.name} {...fadeUp}
                data-testid={`funeral-pkg-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative bg-white rounded-xl p-8 border-2 transition-all hover:-translate-y-1 flex flex-col
                  ${p.highlight ? "border-primary shadow-[0_20px_50px_rgba(201,169,97,0.15)]" : "border-border-warm"}`}>
                {p.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs uppercase tracking-widest px-5 py-1.5 rounded-full whitespace-nowrap">
                    {p.badge}
                  </div>
                )}
                <div className="h-1.5 w-14 bg-primary rounded mb-5"></div>
                <h3 className="font-serif text-3xl mb-2">{p.name}</h3>
                <p className="text-warmgrey text-sm mb-6 min-h-[52px]">{p.tagline}</p>
                <div className="mb-6 min-h-[64px] flex items-center">
                  {unlocked ? (
                    <>
                      <span className="font-serif text-5xl text-headline">${p.price.toLocaleString()}</span>
                      <span className="text-warmgrey text-sm ml-2">AUD</span>
                    </>
                  ) : (
                    <div className="relative">
                      <span className="font-serif text-5xl text-headline/20 select-none filter blur-md" aria-hidden>$X,XXX</span>
                      <div className="absolute inset-0 flex items-center">
                        <LockedPrice onUnlock={() => requestUnlock(p.name)} />
                      </div>
                    </div>
                  )}
                </div>
                {p.inheritsFrom && (
                  <p className="text-sm font-medium text-headline mb-3">Everything in {p.inheritsFrom}, plus:</p>
                )}
                <ul className="space-y-2.5 mb-6 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[14px] text-warmgrey">
                      <Check size={15} style={{ color: "#C9A961" }} className="mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {p.idealFor && (
                  <div className="mb-6 pt-5 border-t border-border-warm">
                    <p className="text-xs uppercase tracking-widest text-warmgrey-muted mb-2">Best for</p>
                    <p className="text-sm text-warmgrey leading-relaxed">{p.idealFor}</p>
                  </div>
                )}
                <a href="#quote-form" className={`w-full justify-center ${p.highlight ? "btn-primary" : "btn-secondary"}`}
                  data-testid={`funeral-pkg-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  Enquire about {p.name}
                </a>
              </motion.div>
            ))}
          </div>

          {/* Home & Hearse Coverage */}
          {coverageAddons.length > 0 && (
            <div className="mt-24">
              <motion.div className="text-center mb-10" {...fadeUp}>
                <p className="eyebrow mb-3">Home &amp; Hearse Coverage</p>
                <h3 className="font-serif text-3xl md:text-4xl">Additional coverage, before and beyond the service.</h3>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                {coverageAddons.map((c) => (
                  <motion.div key={c.name} {...fadeUp}
                    className="bg-white rounded-xl p-8 border border-border-warm flex flex-col"
                    data-testid={`coverage-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="h-1 w-10 bg-primary rounded mb-4"></div>
                    <h4 className="font-serif text-2xl mb-2">{c.name}</h4>
                    <p className="text-warmgrey text-sm mb-4 min-h-[44px]">{c.tagline}</p>
                    <div className="mb-5 min-h-[56px] flex items-center">
                      {unlocked ? (
                        <>
                          <span className="font-serif text-4xl text-headline">${c.price.toLocaleString()}</span>
                          <span className="text-warmgrey text-sm ml-2">AUD</span>
                        </>
                      ) : (
                        <LockedPrice onUnlock={() => requestUnlock(c.name)} size="sm" />
                      )}
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {c.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-warmgrey">
                          <Check size={14} style={{ color: "#C9A961" }} className="mt-1 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <a href="#quote-form" className="btn-outline w-full justify-center text-sm">Enquire about {c.name.split(" — ")[0]}</a>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="section-pad bg-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <motion.div className="text-center mb-14" {...fadeUp}>
            <p className="eyebrow mb-3">Optional Add-Ons</p>
            <h2 className="font-serif text-3xl md:text-4xl">Make the farewell even more complete.</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ADDONS.map((a) => (
              <div key={a.name} className="bg-white border border-border-warm rounded-lg p-6 flex justify-between items-center" data-testid={`addon-${a.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="text-headline font-medium">{a.name}</span>
                {unlocked ? (
                  <span className="font-serif text-lg" style={{ color: "#C9A961" }}>{a.price}</span>
                ) : (
                  <LockedPrice onUnlock={() => requestUnlock(a.name)} size="sm" />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-warmgrey-muted text-sm mt-8 max-w-3xl mx-auto">
            Additional travel, extended coverage and special production requirements may incur additional charges. All additional costs will be discussed and confirmed before the booking.
          </p>
        </div>
      </section>

      {/* SPECIAL OFFER */}
      <section className="section-pad bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <motion.div className="grid md:grid-cols-2 gap-14 items-center" {...fadeUp}>
            <div className="relative">
              <img src={IMG.legacy} alt="Family preserving memories at home" className="rounded-xl w-full h-[460px] object-cover shadow-[0_10px_40px_rgba(0,0,0,0.08)]" />
              <div className="absolute -bottom-5 -right-5 hidden md:flex items-center gap-2 bg-secondary text-white text-xs uppercase tracking-widest px-4 py-2 rounded-full">
                <Sparkles size={14} style={{ color: "#C9A961" }} /> A Family Benefit
              </div>
            </div>
            <div>
              <p className="eyebrow mb-3">A Special Gift for Your Loved One</p>
              <h2 className="font-serif text-4xl md:text-5xl mb-6 leading-tight">Book Your Funeral Service &amp; Receive <em style={{ color: "#C9A961" }}>Up to 15% Off</em> Family Legacy Films.</h2>
              <p className="font-serif italic text-xl text-headline mb-6">
                "The funeral captures the farewell. A Family Legacy Film preserves the life."
              </p>
              <p className="text-warmgrey text-lg leading-relaxed mb-6">
                When you book your funeral livestream or funeral video service with Family Legacy Films, your family may receive
                up to 15% off selected Family Legacy Films packages. It's our way of helping families preserve something that lasts beyond the day of the funeral.
              </p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-warmgrey mb-8">
                {[
                  "Childhood and early years", "Family history", "Achievements",
                  "Stories from children & grandchildren", "Messages from loved ones",
                  "Favourite photographs & videos", "Family traditions", "Special memories",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2"><Check size={14} style={{ color: "#C9A961" }} className="mt-1 shrink-0" /><span>{t}</span></li>
                ))}
              </ul>
              <Link to="/legacy-films" className="btn-primary" data-testid="funeral-legacy-cta">Discover Family Legacy Films <ArrowRight size={16} /></Link>
              <p className="text-xs text-warmgrey-muted mt-4 max-w-md">Discount applies to eligible Family Legacy Films packages and is subject to package availability and terms. Cannot be combined with other promotions unless agreed in writing.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-pad bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div className="text-center mb-16" {...fadeUp}>
            <p className="eyebrow text-primary mb-3">More Than a Funeral Recording</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white">We help families preserve what matters.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_US.map((b, i) => (
              <motion.div key={b.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white/5 border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-colors" data-testid={`why-${i}`}>
                <b.icon size={28} style={{ color: "#C9A961" }} className="mb-5" />
                <h3 className="font-serif text-2xl mb-2 text-white">{b.t}</h3>
                <p className="text-white/70 leading-relaxed">{b.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-pad bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div className="text-center mb-16" {...fadeUp}>
            <p className="eyebrow mb-3">How It Works</p>
            <h2 className="font-serif text-4xl md:text-5xl">Simple. Professional. One less thing to worry about.</h2>
          </motion.div>
          <div className="grid md:grid-cols-5 gap-6 md:gap-4">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center px-3" data-testid={`step-${s.n}`}>
                <div className="font-serif text-5xl mb-3" style={{ color: "#C9A961" }}>{s.n}</div>
                <h3 className="font-serif text-xl mb-3">{s.t}</h3>
                <p className="text-warmgrey text-sm leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="section-pad bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-center">
          <motion.img {...fadeUp} src={IMG.pallbearers} alt={`Pallbearers carrying a coffin at a ${city} funeral`} className="rounded-xl w-full h-[420px] object-cover" />
          <motion.div {...fadeUp}>
            <p className="eyebrow mb-3">{city} Coverage</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-6">Funeral livestreaming across {city}.</h2>
            <p className="text-warmgrey text-lg mb-8 leading-relaxed">
              We cover churches, chapels, funeral homes, crematoria and graveside services right across {city} and surrounding areas.
              If your funeral is outside these areas, contact us — we may still be able to provide coverage.
            </p>
            <div className="flex flex-wrap gap-2">
              {areas.map((a) => (
                <span key={a} className="inline-flex items-center gap-1 border border-border-warm bg-cream rounded-full px-4 py-1.5 text-sm text-headline" data-testid={`area-${a.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                  <MapPin size={12} style={{ color: "#C9A961" }} /> {a}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <p className="eyebrow mb-3">Frequently Asked Questions</p>
            <h2 className="font-serif text-4xl md:text-5xl">Everything you might be wondering.</h2>
          </motion.div>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q${i}`} className="border-b border-border-warm" data-testid={`faq-${i}`}>
                <AccordionTrigger className="font-serif text-xl text-left py-6 hover:no-underline text-headline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-warmgrey text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 overflow-hidden">
        <img src={IMG.graveside} alt="A family gathers at a graveside service" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,20,26,0.55) 0%, rgba(17,20,26,0.85) 100%)" }}></div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <motion.div {...fadeUp}>
            <p className="eyebrow text-primary mb-4">Need a funeral livestream in {city}?</p>
            <h2 className="font-serif text-4xl md:text-6xl text-white mb-6">Let everyone say goodbye.</h2>
            <p className="text-white/85 text-lg md:text-xl mb-10 leading-relaxed">
              You take care of your family. We'll take care of the livestream.<br />Tell us the date, location and type of service and we'll help you choose the right package.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#quote-form" className="btn-primary" data-testid="final-cta-quote">Get a Quote</a>
              <a href={`tel:${phone}`} className="btn-outline text-white border-white/70 hover:bg-white hover:text-secondary" data-testid="final-cta-call">
                <Phone size={16} /> Call Us
              </a>
            </div>
            <p className="text-white/50 text-xs mt-6">Urgent bookings welcome, subject to availability.</p>
          </motion.div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="section-pad bg-cream" id="quote-form">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <motion.div className="text-center mb-10" {...fadeUp}>
            <p className="eyebrow mb-3">Request a Quote · {city}</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Tell us about the service.</h2>
            <p className="text-warmgrey text-lg">We respond personally, usually within the hour during the day.</p>
          </motion.div>

          {done ? (
            <div id="quote-thanks" className="bg-white p-10 rounded-xl border border-border-warm text-center" data-testid="quote-thanks">
              <Heart size={36} style={{ color: "#C9A961" }} className="mx-auto mb-5" />
              <h3 className="font-serif text-3xl mb-3">Thank you.</h3>
              <p className="text-warmgrey">Your request is with us. We will call you back very shortly.</p>
              <div className="mt-6"><a href={`tel:${phone}`} className="btn-primary"><Phone size={16} /> Call us now</a></div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white p-8 md:p-10 rounded-xl border border-border-warm space-y-4" data-testid="quote-form-el">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Name *</label>
                  <input required data-testid="q-name" className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Phone *</label>
                  <input required data-testid="q-phone" className="input-field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Email *</label>
                <input required type="email" data-testid="q-email" className="input-field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Funeral date</label>
                  <input data-testid="q-date" className="input-field" placeholder="e.g. Sat 14 Dec, 10am" value={form.funeral_date} onChange={(e) => setForm({ ...form, funeral_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Funeral location</label>
                  <input data-testid="q-location" className="input-field" placeholder="Suburb, church or funeral home" value={form.funeral_location} onChange={(e) => setForm({ ...form, funeral_location: e.target.value })} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Service type</label>
                  <select data-testid="q-service" className="input-field" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
                    <option value="">Select…</option>
                    <option>Church / Chapel</option>
                    <option>Funeral home</option>
                    <option>Graveside</option>
                    <option>Crematorium</option>
                    <option>Memorial service</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Package interest</label>
                  <select data-testid="q-package" className="input-field" value={form.package_interest} onChange={(e) => setForm({ ...form, package_interest: e.target.value })}>
                    <option value="">Not sure yet</option>
                    {packages.map((p) => <option key={p.name} value={unlocked ? `${p.name} — $${p.price.toLocaleString()}` : p.name}>{p.name}{unlocked ? ` — $${p.price.toLocaleString()}` : ""}</option>)}
                    <option>Custom / discuss</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Overseas or interstate family?</label>
                <select data-testid="q-overseas" className="input-field" value={form.overseas_family} onChange={(e) => setForm({ ...form, overseas_family: e.target.value })}>
                  <option value="">Select…</option>
                  <option>Yes — overseas</option>
                  <option>Yes — interstate</option>
                  <option>Yes — both</option>
                  <option>No</option>
                  <option>Not sure</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Additional information</label>
                <textarea data-testid="q-info" className="input-field min-h-[130px]" placeholder="Anything else that would help us prepare…" value={form.additional_info} onChange={(e) => setForm({ ...form, additional_info: e.target.value })} />
              </div>
              <button disabled={loading} className="btn-primary w-full justify-center" data-testid="q-submit">
                {loading ? "Sending…" : "Request Your Quote"}
              </button>
              <p className="text-xs text-warmgrey-muted text-center">Your details are handled with care and never shared. Available for urgent bookings — please also call us on <a href={`tel:${phone}`} className="underline">{phoneDisplay}</a>.</p>
            </form>
          )}
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-secondary/95 backdrop-blur border-t border-white/10 px-4 py-3 flex gap-2" data-testid={`funeral-${cityLower}-mobile-cta`}>
        <a href="#quote-form" className="btn-primary flex-1 justify-center text-sm" data-testid="mobile-cta-quote">Get a Quote</a>
        <a href={`tel:${phone}`} className="btn-outline text-white border-white/40 justify-center px-4" data-testid="mobile-cta-call" aria-label="Call us"><Phone size={16} /></a>
      </div>

      {showUnlock && (
        <UnlockModal
          city={city}
          defaultInterest={unlockContext}
          onClose={() => setShowUnlock(false)}
          onUnlocked={() => { setUnlocked(true); setShowUnlock(false); }}
        />
      )}
    </div>
  );
}
